import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { MetadataItem } from '../AgentsAdmin/useAgentMetadata';
import ResourceMentionEditor from './ResourceMentionEditor';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: (createdSkill: unknown) => void;
  resources: MetadataItem[];
}

const CreateCustomSkillModal: FC<Props> = ({
  isOpen,
  toggle,
  onSuccess,
  resources,
}) => {
  const { formatMessage } = useIntl();

  const handleCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        toast.success(
          formatMessage({
            id: 'app.views.ai.agents.skills_admin.create_modal.success',
            defaultMessage: 'Custom skill created successfully',
          })
        );
        onSuccess(data);
        toggle();
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.skills_admin.create_modal.error',
            defaultMessage: 'Failed to create custom skill',
          })
        );
      }
    },
    [formatMessage, onSuccess, toggle]
  );

  const inputs = useMemo(
    () => [
      {
        required: true,
        name: 'name',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.name.label',
          defaultMessage: 'Skill Name',
        }),
        placeholder: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.name.placeholder',
          defaultMessage: 'Enter skill name',
        }),
        type: INPUT_TYPES.TEXT,
        ignorePasswordManagers: true,
        autoFocus: true,
      },
      {
        required: false,
        name: 'description',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.description.label',
          defaultMessage: 'Description',
        }),
        helperText: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.description.helper_text',
          defaultMessage: 'A brief summary of what this skill does.',
        }),
        placeholder: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.description.placeholder',
          defaultMessage: 'Enter a description (optional)',
        }),
        type: INPUT_TYPES.TEXTAREA,
      },
      {
        required: true,
        name: 'instructions',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.instructions.label',
          defaultMessage: 'Instructions',
        }),
        helperText: formatMessage({
          id: 'app.views.ai.agents.skills_admin.create_modal.fields.instructions.helper_text',
          defaultMessage:
            'Write instructions for the AI agent. Use @ to reference available resources.',
        }),
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: (props) => (
          <ResourceMentionEditor
            value={props.value || ''}
            onChange={props.onChange}
            resources={resources}
          />
        ),
      },
    ],
    [formatMessage, resources]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      object={{}}
      inputs={inputs}
      method="POST"
      url="ai-custom-skills"
      submitText={formatMessage({
        id: 'app.views.ai.agents.skills_admin.create_modal.submit_text',
        defaultMessage: 'Create',
      })}
      title={formatMessage({
        id: 'app.views.ai.agents.skills_admin.create_modal.title',
        defaultMessage: 'Create Custom Skill',
      })}
      callback={handleCallback}
      keepOpenOnSuccess={true}
    />
  );
};

export default CreateCustomSkillModal;
